import React, { useState, useRef } from "react";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { CustomDialogTemplate } from "../../../shared/components/CustomDialogTemplate";
import InfoIcon from "@mui/icons-material/Info";
import { InputControl } from "../../../shared/components/InputControl";
import { addDays } from "date-fns";
import SimpleReactValidator from "simple-react-validator";
import dayjs from "dayjs";

export function PosScheduleSyncModal({
  open,
  onClose,
  onSave,
  syncType,
  setSyncType,
  scheduledDate,
  setScheduledDate,
  isBtnLoading,
}) {
  const [_, forceUpdate] = useState(0);
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate },
      element: (message) => message,
      validators: {
        notPastDate: {
          message: "Date must be in the future.",
          rule: (val, params) => {
            const syncType = params && params[0];
            if (syncType !== "scheduled") return true;
            if (!val) return false;

            const selected = dayjs(val);
            const today = dayjs().startOf("day");

            if (!selected.isValid()) return false;

            return selected.isAfter(today, "day");
          },
          required: false,
        },
      },
    }),
  );

  const dateHelperText = formValidator.current.message(
    "date",
    scheduledDate,
    syncType === "scheduled"
      ? `required|notPastDate:${syncType}`
      : "notPastDate:immediate",
  );

  const handleSave = (e) => {
    e.preventDefault();
    console.log(e, formValidator.current);

    if (formValidator.current.allValid()) {
      onSave?.({
        syncType,
        scheduledDate: syncType === "scheduled" ? scheduledDate : null,
      });
    } else {
      formValidator.current.showMessages();
      forceUpdate(_ + 1);
    }
  };

  return (
    <CustomDialogTemplate
      PaperProps={{
        sx: {
          minWidth: { md: "636px", sm: "80%", xs: "100%" },
          height: "459px",
        },
      }}
      title={
        <Stack direction="row" alignItems="center" gap="8px">
          <img
            src="/icons/ic_warning.svg"
            alt="Schedule Sync"
            style={{
              padding: "6px",
              backgroundColor: "#F044381F",
              borderRadius: "50%",
            }}
            width={32}
            height={32}
          />
          <Typography variant="h6" fontWeight={600}>
            Confirm Update
          </Typography>
        </Stack>
      }
      closeIcon={false}
      open={open}
      content={
        <Stack
          sx={{
            p: "16px 24px",
            gap: "12px",
            height: "100%",
          }}
        >
          <Typography>
            This change may impact the cost of one or more recipes. If the cost
            of a Finish Product–type recipe is affected, please confirm when the
            updated cost should be pushed to the POS.
          </Typography>

          <RadioGroup
            value={syncType}
            onChange={(e) => setSyncType(e.target.value)}
          >
            <Stack gap="4px" py={1}>
              <Stack direction="row" alignItems="center" gap="4px" py={1}>
                <FormControlLabel
                  value="immediate"
                  control={<Radio />}
                  label={
                    <Stack gap="2px">
                      <Typography variant="body2" fontWeight={500}>
                        Sync Immediately
                      </Typography>
                      <Typography variant="caption" color="#485364">
                        Updates the POS as soon as the cost is saved.
                      </Typography>
                    </Stack>
                  }
                />
              </Stack>

              <Stack direction="row" alignItems="flex-start" gap="4px" py={1}>
                <FormControlLabel
                  value="scheduled"
                  control={<Radio />}
                  sx={{ alignItems: "flex-start" }}
                  label={
                    <Stack gap="2px" alignItems="flex-start">
                      <Typography variant="body2" fontWeight={500}>
                        Schedule Sync
                      </Typography>
                      <Typography variant="caption" color="#485364">
                        Sync the updated cost to the POS at a later time.
                      </Typography>
                      <InputControl
                        type="datePicker"
                        size="small"
                        label="Select Date"
                        sx={{ mt: 1 }}
                        helperText={dateHelperText}
                        error={!!dateHelperText}
                        customDatePickerIcon
                        datePickerProps={{
                          disableMaskedInput: true,
                          inputFormat: "dd, MMM yyyy",
                          minDate: addDays(new Date(), 1),
                          value: scheduledDate || null,
                          onChange: (date) => setScheduledDate(date),
                        }}
                      />
                    </Stack>
                  }
                />
              </Stack>
            </Stack>
          </RadioGroup>

          <Stack direction="row">
            <InfoIcon sx={{ height: "13px", color: "#485364", mt: 0.5 }} />
            <Typography color="#485364" fontSize="14px" fontWeight={400}>
              Cancelling this action will discard all current updates, and no
              cost changes will be saved.
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-end"
            padding="10px"
            marginTop="auto"
            gap="8px"
          >
            <Button
              key="cancel"
              variant="outlined"
              color="gray2"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              key="generate"
              variant="contained"
              onClick={handleSave}
              disabled={!syncType}
            >
              {isBtnLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "save"
              )}
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
}
